import { ArrowRight, Hourglass } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useChartDates, useForgottenPreview, useOnThisDay, useStations } from "../api";
import { ArtistLinks } from "../components/ArtistLinks";
import { Cover } from "../components/Cover";
import { StationLogo } from "../components/StationLogo";
import { DatePicker } from "../components/DatePicker";
import { SearchBox } from "../components/SearchBox";
import { appearances, longDate, plural, yearRange } from "../format";
import { useTitle } from "../hooks";

export function Home() {
  useTitle();
  const stations = useStations();
  const today = useOnThisDay();
  const forgotten = useForgottenPreview();
  const totalEditions = stations.data?.stations
    .flatMap((s) => s.charts)
    .reduce((n, c) => n + c.editions, 0);

  return (
    <>
      <section className="hero">
        <h1>
          Znajdź piosenkę, o której <em>zapomniałeś</em>.
        </h1>
        <p className="lead">
          Archiwum polskich radiowych list przebojów. Szukaj po tytule lub
          wykonawcy, cofnij się w czasie albo daj się zaskoczyć.
        </p>
        <SearchBox large autoFocus />
        {totalEditions ? (
          <dl className="hero-stats">
            <div>
              <dt>Notowań</dt>
              <dd>{totalEditions.toLocaleString("pl-PL")}</dd>
            </div>
            <div>
              <dt>Stacji</dt>
              <dd>{stations.data?.stations.length}</dd>
            </div>
            <div>
              <dt>Od roku</dt>
              <dd>
                {Math.min(
                  ...(stations.data?.stations
                    .flatMap((s) => s.charts)
                    .map((c) => Number(c.firstDate?.slice(0, 4) ?? 9999)) ?? [9999]),
                )}
              </dd>
            </div>
          </dl>
        ) : null}
      </section>

      {stations.data && <TimeMachine stations={stations.data.stations} />}

      <section aria-labelledby="otd">
        <h2 id="otd">Dziś w historii</h2>
        <p className="muted">Numery jeden z tego dnia roku.</p>
        {today.data && today.data.numberOnes.length === 0 && (
          <p className="muted">Brak notowań z tego dnia.</p>
        )}
        <ul className="cards">
          {today.data?.numberOnes.slice(0, 8).map((n) => (
            <li key={`${n.chart}-${n.date}`} className="card">
              <Cover
                url={n.track.imageUrl}
                id={n.track.id}
                title={n.track.title}
                size={64}
                play={n.track}
              />
              <div>
                <div className="card__kicker">
                  {longDate(n.date)} · {n.stationName}
                </div>
                <Link className="card__title" to={`/utwor/${n.track.id}`}>
                  {n.track.title}
                </Link>
                <div className="card__sub">
                  <ArtistLinks
                    artists={n.track.artists}
                    fallback={n.track.credit}
                  />
                </div>
                <Link className="card__link" to={`/listy/${n.chart}/${n.date}`}>
                  Zobacz całą listę <ArrowRight size={14} aria-hidden="true" />
                </Link>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="forgotten">
        <div className="section-head">
          <h2 id="forgotten">Zapomniane hity</h2>
          <Link to="/odkrywaj" className="link-arrow">Więcej i filtry <ArrowRight size={16} aria-hidden="true" /></Link>
        </div>
        <p className="muted">Długo gościły na listach, a od lat ich nie ma.</p>
        <ul className="cards">
          {forgotten.data?.tracks.map((t) => (
            <li key={t.id} className="card">
              <Cover url={t.imageUrl} id={t.id} title={t.title} size={64} play={t} />
              <div>
                <div className="card__kicker">
                  {yearRange(t.firstDate, t.lastDate)} ·{" "}
                  {appearances(t.entries)}
                </div>
                <Link className="card__title" to={`/utwor/${t.id}`}>
                  {t.title}
                </Link>
                <div className="card__sub">
                  <ArtistLinks artists={t.artists} fallback={t.credit} />
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="stations">
        <h2 id="stations">Listy w archiwum</h2>
        <ul className="stations">
          {stations.data?.stations
            .flatMap((s) => s.charts.map((c) => ({ s, c })))
            .map(({ s, c }, i) => (
              <li key={c.slug}>
                <Link
                  to={`/listy/${c.slug}`}
                  className="station"
                  style={{ "--chart-color": `var(--c${(i % 6) + 1})` } as React.CSSProperties}
                >
                  <StationLogo slug={s.slug} size={44} />
                  <span className="station__name">{s.name}</span>
                  <span className="station__chart">{c.name}</span>
                  <span className="station__meta">
                    {c.editions.toLocaleString("pl-PL")}{" "}
                    {plural(c.editions, "notowanie", "notowania", "notowań")}
                    {c.firstDate && c.lastDate
                      ? ` · ${yearRange(c.firstDate, c.lastDate)}`
                      : ""}
                  </span>
                </Link>
              </li>
            ))}
        </ul>
      </section>
    </>
  );
}

function TimeMachine({
  stations,
}: {
  stations: {
    slug: string;
    name: string;
    charts: {
      slug: string;
      name: string;
      firstDate: string | null;
      lastDate: string | null;
    }[];
  }[];
}) {
  const charts = stations.flatMap((s) =>
    s.charts.map((c) => ({ ...c, station: s.name })),
  );
  const [slug, setSlug] = useState(
    charts.find((c) => c.slug === "rmf-fm-poplista")?.slug ??
      charts[0]?.slug ??
      "",
  );
  const chart = charts.find((c) => c.slug === slug);
  const [date, setDate] = useState("2003-06-15");
  const dates = useChartDates(slug, true);

  // Start from a day that really has a chart: the closest one to the date we like.
  useEffect(() => {
    const list = dates.data?.dates;
    if (!list?.length || list.includes(date)) return;
    setDate(list.find((d) => d >= date) ?? list[list.length - 1]!);
  }, [dates.data, date]);
  const navigate = useNavigate();

  return (
    <section aria-labelledby="tm" className="panel">
      <h2 id="tm"><Hourglass size={22} aria-hidden="true" /> Wehikuł czasu</h2>
      <form
        className="row row--tm"
        onSubmit={(e) => {
          e.preventDefault();
          navigate(`/listy/${slug}/${date}`);
        }}
      >
        <label>
          Lista
          <select value={slug} onChange={(e) => setSlug(e.target.value)}>
            {charts.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.station} {c.name}
              </option>
            ))}
          </select>
        </label>
        <div className="field">
          <span className="field__label">Dzień</span>
          <DatePicker label="Wybierz dzień" value={date} dates={dates.data?.dates} onChange={setDate} align="start" />
        </div>
        <button type="submit" className="primary">
          Pokaż, co grało
        </button>
      </form>
      <p className="muted small">
        Jeśli tego dnia nie było notowania, pokażemy najbliższe.
      </p>
    </section>
  );
}
